/**
 * 모바일 하단 네비게이션 컴포넌트
 *
 * 모바일 화면에서만 하단에 표시되는 네비게이션 바.
 * - 농약잔류, 동약잔류, 홈, 한영토글, 로그인
 * - useResponsive 훅으로 모바일 여부 판별
 */
import React from 'react';
import { message } from 'antd';
import {
  HomeOutlined,
  ExperimentOutlined,
  MedicineBoxOutlined,
  GlobalOutlined,
  LoginOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsLoggedIn, showLoginModal, logout } from '../../store/authSlice';
import useResponsive from '../../hooks/useResponsive';
import useLanguage from '../../hooks/useLanguage';

// ─── 다국어 텍스트 ──────────────────────────────────────────
const TEXTS = {
  ko: {
    prdMrl: '농약잔류',
    vdMrl: '동약잔류',
    home: '홈',
    lang: 'EN',
    login: '로그인',
    logout: '로그아웃',
  },
  en: {
    prdMrl: 'Pest MRL',
    vdMrl: 'Vet MRL',
    home: 'Home',
    lang: '한국어',
    login: 'Login',
    logout: 'Logout',
  },
};

/**
 * 모바일 하단 네비게이션
 */
const MobileBottomNav = () => {
  const { isMobile } = useResponsive();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { t, language, setLanguage } = useLanguage(TEXTS);

  if (!isMobile) return null;

  /** 현재 경로에 해당하는 활성 키 */
  const getActiveKey = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/prd')) return 'prd';
    if (path.startsWith('/vd')) return 'vd';
    return 'home';
  };

  const activeKey = getActiveKey();

  const handleNav = (key) => {
    if (key === 'prd') history.push('/prd/mrls');
    else if (key === 'vd') history.push('/vd/mrls');
    else if (key === 'home') history.push('/');
    else if (key === 'lang') setLanguage(language === 'ko' ? 'en' : 'ko');
    else if (key === 'auth') {
      if (isLoggedIn) {
        dispatch(logout()).then(() => {
          message.success('로그아웃되었습니다.');
          history.push('/');
        });
      } else {
        dispatch(showLoginModal());
      }
    }
  };

  const items = [
    { key: 'prd', label: t.prdMrl, icon: <ExperimentOutlined /> },
    { key: 'vd', label: t.vdMrl, icon: <MedicineBoxOutlined /> },
    { key: 'home', label: t.home, icon: <HomeOutlined /> },
    { key: 'lang', label: t.lang, icon: <GlobalOutlined /> },
    {
      key: 'auth',
      label: isLoggedIn ? t.logout : t.login,
      icon: isLoggedIn ? <LogoutOutlined /> : <LoginOutlined />,
    },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid #e8e8e8',
        zIndex: 1000,
        paddingBottom: 'env(safe-area-inset-bottom)',
        display: 'flex',
      }}
    >
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            onClick={() => handleNav(item.key)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 56,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: isActive ? '#2F6DB3' : '#888',
              fontSize: 10,
              fontFamily: 'inherit',
              gap: 2,
              padding: 0,
              transition: 'color 0.2s',
            }}
          >
            <span style={{ fontSize: 20 }}>{item.icon}</span>
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default MobileBottomNav;
